import React, { useRef, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { createSelector } from 'reselect';

import { getMeasurableFields } from '../../../lib/exporterHelper';
import GridLayout from '../GridLayout';
import FieldModes from '../../../constants/FieldModes';

const StyledWrapper = styled.div`
  display: flex;
  width: ${(props) => props.width}px;
  height: ${(props) => props.height}px;
`;

/**
 * ExportFormPage handles specific requirements around formatting
 * for PDF Export. For instance, it measures the fields to dispaly
 * all internal contents, it adjusts the pages to support proper
 * page-break points, etc.
 */
const ExportFormPage = (props) => {

  const {

    id, //Page._id

    //PDF Config
    config,

    //Page Properties
    cols,
    fields,

    /**
     * What does margin do? This was a legacy implementation that we had for supporting the external whitespace from
     * the border that was visible on a page. This is something that we implemented for honeywell POC but no longer use.
     */
    rowHeight,
    margin,
    padding,
    borderWidth,

    licenseValidation,
    measure,
    onMeasurementsCompleted

  } = props;


  /**
   * Handle Meaurement For Page With No MeasurableFields
   * ==================================================
   * 
   * IMPORTANT NOTE: DO NOT REMOVE. This is used to trigger measurement completed if measurement is required and there are no valid fields 
   * or no measurable fields. The PDF Exporter waits for each page to be measured before rendering the PDF and call onMeasurementsCompleted
   * even if there are no measurable fields.
   */
  useEffect(() => {
    const measureableFields = fields && fields.length > 0 ? getMeasurableFieldSelector({ fields, licenseValidation }) : [];
    if (measureableFields.length < 1 && onMeasurementsCompleted) onMeasurementsCompleted(id, {});
  }, [fields]);

  /**
   * Handle Meaurement For Page With Measurable Fields
   * ================================================
   * 
   * This is used to listen for the measurement events from each individual measurable field. Once all 
   * fields have been measured, we call onMeasurementsCompleted with the measurements for the page.
   */
  const measurementsRef = useRef({});
  const handleFieldMeasurementCompleted = useCallback((fieldId, fieldIdentifier, fieldPositionId, rect) => {
    const measureableFields = getMeasurableFieldSelector({ fields, licenseValidation });


    /**
     * Handle measured height
     *
     * Convert to proper react-grid-layout row height.
     *
     * Why +4? The field has a transparent border of 1px all around
     * and 1px padding all around that is not accounted for in the
     * internal height calculation.
     */
    const measuredHeight = (rect.height + 4) / rowHeight;

    const nextRect = { ...rect, width: Math.ceil(rect.width + 4), height: Math.ceil(measuredHeight) };
    measurementsRef.current[fieldPositionId] = nextRect;

    const measureableFieldFound = measureableFields.find((field) => field._id === fieldPositionId);
    if (!measureableFieldFound) console.log('ExportFormPage.handleFieldMeasurementCompleted: ', fieldPositionId, !!measureableFieldFound, measureableFieldFound);

    /**
     * Only update measurements once all fields have been measured.
     */
    if (Object.keys(measurementsRef.current).length === measureableFields.length && onMeasurementsCompleted) {
      onMeasurementsCompleted(id, { ...measurementsRef.current });
    }

  }, [fields, licenseValidation, rowHeight]);

  /**
   * Handle container max width and height adjustments
   *
   * The page margin, padding, and border should be applied to the container and
   * reduce the max dimensions of the container element.
   *
   * Why is this? The container element determines the max dimensions that the
   * fields and PDF background can be within. The border, padding, and margin of
   * the page itself reduce that max dimension limit. Therefore we apply those
   * page properties to the container since that is what enforces those dimensions
   * for the page fields and PDF background.
   *
   * For instance if the container max width is 816px and the page has padding of
   * 4px (x2 for both sides) then the max width of the container should be reduced
   * to 804px. Then we can scale the PDF background and fields to fit within that
   * new 804px width limit.
   */
  const adjustedContainerWidth = getAdjustedDimension(config.page.width, (padding + config.page.padding), margin, borderWidth);

  return (
    <StyledWrapper {...{ width: adjustedContainerWidth }}>
      <GridLayout
        {...props}
        mode={FieldModes.pdf}
        fields={fields}
        width={adjustedContainerWidth}
        cols={cols}
        enableLazyLoadFields={false}
        licenseValidation={licenseValidation}
        enableFieldMeasurementRendering
        onFieldMeasurementCompleted={handleFieldMeasurementCompleted}
      />
    </StyledWrapper>
  );

};

export const getMeasurableFieldSelector = createSelector(
  (state) => state.fields,
  (state) => state.licenseValidation,
  (fields, licenseValidation) => {
    return getMeasurableFields(fields, licenseValidation);
  }
);

/**
 * What is 2 used for?
 *
 * We do (* 2) to represent the number of sides of the page to take into account.
 *
 * For instance if you want to adjust the height of the page by subtracting the margin
 * from top and bottom of the page then you would use a multiple of 2.
 */
const getAdjustedDimension = (value, padding, margin, borderWidth) => {

  let pageValue = value;
  if (padding) pageValue -= (padding * 2);
  if (borderWidth) pageValue -= (borderWidth * 2);
  if (margin) pageValue -= (margin * 2);

  return pageValue;

};

export default ExportFormPage;
